import ReactGA from 'react-ga4';
import { getGA4MeasurementId, isGA4DebugMode, logGA4Config } from '@/config/ga4.resolver';

// Types
export interface GAEventParams {
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

export interface UserProperties {
  user_id?: string;
  user_role?: 'admin' | 'learner' | string;
  [key: string]: any;
}

// Initialize GA4
let isInitialized = false;

export const initGA = (): void => {
  // Don't initialize if already initialized
  if (isInitialized) {
    return;
  }

  // Get measurement ID from resolver (supports AUTO mode with domain mapping)
  const measurementId = getGA4MeasurementId();
  const debugMode = isGA4DebugMode();

  // Don't initialize if no measurement ID
  if (!measurementId) {
    console.warn('[GA4] No measurement ID configured for this domain. Check ga4.resolver.ts or set VITE_GA4_MEASUREMENT_ID');
    return;
  }

  // Log configuration in development
  if (debugMode) {
    logGA4Config();
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        // Privacy & compliance
        allow_google_signals: true,
        allow_ad_personalization_signals: false,

        // Cookie settings
        cookie_domain: 'auto',
        cookie_expires: 63072000, // 2 years
        cookie_flags: 'SameSite=None;Secure',

        // Page tracking (manual via RouteTracker)
        send_page_view: false,
      },
      gtagOptions: {
        // Debug mode based on resolver
        debug_mode: debugMode,
      },
    });

    isInitialized = true;
    console.log(`[GA4] Initialized successfully with ID: ${measurementId} (debug: ${debugMode})`);
  } catch (error) {
    console.error('[GA4] Initialization failed:', error);
  }
};

// Check if GA is initialized
export const isGAInitialized = (): boolean => {
  return isInitialized;
};

// Track page view
export const trackPageView = (path?: string, title?: string): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path || window.location.pathname,
      title: title || document.title,
    });
    console.log('[GA4] Page view tracked:', path || window.location.pathname);
  } catch (error) {
    console.error('[GA4] Page view tracking failed:', error);
  }
};

// Track custom event
export const trackEvent = (
  eventName: string,
  params?: GAEventParams
): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.event(eventName, params);
    console.log('[GA4] Event tracked:', eventName, params);
  } catch (error) {
    console.error('[GA4] Event tracking failed:', error);
  }
};

// Set user properties
export const setUserProperties = (properties: UserProperties): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.set(properties);
    console.log('[GA4] User properties set:', properties);
  } catch (error) {
    console.error('[GA4] Setting user properties failed:', error);
  }
};

// Set user ID (for authenticated users)
export const setUserId = (userId: string | null): void => {
  if (!isGAInitialized()) return;

  try {
    if (userId) {
      ReactGA.set({ user_id: userId });
      console.log('[GA4] User ID set:', userId);
    } else {
      // Clear user ID on logout
      ReactGA.set({ user_id: undefined });
      console.log('[GA4] User ID cleared');
    }
  } catch (error) {
    console.error('[GA4] Setting user ID failed:', error);
  }
};

// Track login event
export const trackLogin = (
  userId: string,
  method: 'email' | 'sso' | 'oauth' = 'email',
  role?: 'admin' | 'learner' | string
): void => {
  if (!isGAInitialized()) return;

  try {
    // Set user ID
    setUserId(userId);

    // Set user role if provided
    if (role) {
      setUserProperties({ user_role: role });
    }

    // Track login event
    trackEvent('login', {
      method,
      user_id: userId,
      ...(role && { role }),
    });

    console.log('[GA4] Login tracked:', { userId, method, role });
  } catch (error) {
    console.error('[GA4] Login tracking failed:', error);
  }
};

// Track logout event
export const trackLogout = (userId?: string): void => {
  if (!isGAInitialized()) return;

  try {
    // Track logout event with user ID if available
    trackEvent('logout', {
      ...(userId && { user_id: userId }),
    });

    // Clear user ID
    setUserId(null);

    console.log('[GA4] Logout tracked:', userId);
  } catch (error) {
    console.error('[GA4] Logout tracking failed:', error);
  }
};

// ============================================
// USER BEHAVIOR TRACKING
// ============================================

/**
 * Track button or link clicks
 */
export const trackClick = (
  elementName: string,
  elementType: 'button' | 'link' | 'icon' = 'button',
  additionalData?: GAEventParams
): void => {
  trackEvent('click', {
    element_name: elementName,
    element_type: elementType,
    ...additionalData,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (
  formName: string,
  success: boolean,
  errorMessage?: string
): void => {
  trackEvent('form_submit', {
    form_name: formName,
    success,
    ...(errorMessage && { error_message: errorMessage }),
  });
};

/**
 * Track form field interactions
 */
export const trackFormInteraction = (
  formName: string,
  fieldName: string,
  action: 'focus' | 'blur' | 'change'
): void => {
  trackEvent('form_interaction', {
    form_name: formName,
    field_name: fieldName,
    action,
  });
};

/**
 * Track search queries
 */
export const trackSearch = (
  query: string,
  category?: string,
  resultsCount?: number
): void => {
  trackEvent('search', {
    search_term: query,
    ...(category && { search_category: category }),
    ...(resultsCount !== undefined && { results_count: resultsCount }),
  });
};

/**
 * Track filter and sort actions
 */
export const trackFilter = (
  filterType: string,
  filterValue: string,
  context?: string
): void => {
  trackEvent('filter_applied', {
    filter_type: filterType,
    filter_value: filterValue,
    ...(context && { context }),
  });
};

export const trackSort = (
  sortField: string,
  sortOrder: 'asc' | 'desc',
  context?: string
): void => {
  trackEvent('sort_applied', {
    sort_field: sortField,
    sort_order: sortOrder,
    ...(context && { context }),
  });
};

/**
 * Track modal/dialog interactions
 */
export const trackModal = (
  modalName: string,
  action: 'open' | 'close' | 'confirm' | 'cancel'
): void => {
  trackEvent('modal_interaction', {
    modal_name: modalName,
    action,
  });
};

/**
 * Track tab changes
 */
export const trackTabChange = (tabName: string, context?: string): void => {
  trackEvent('tab_change', {
    tab_name: tabName,
    ...(context && { context }),
  });
};

/**
 * Track user engagement - scroll depth
 */
export const trackScrollDepth = (
  percentage: number,
  page?: string
): void => {
  trackEvent('scroll_depth', {
    scroll_percentage: percentage,
    page: page || window.location.pathname,
  });
};

/**
 * Track time spent on page
 */
export const trackTimeOnPage = (
  timeInSeconds: number,
  page?: string
): void => {
  trackEvent('time_on_page', {
    time_seconds: timeInSeconds,
    page: page || window.location.pathname,
  });
};

/**
 * Track navigation patterns
 */
export const trackNavigation = (
  from: string,
  to: string,
  method: 'link' | 'button' | 'menu' | 'breadcrumb' = 'link'
): void => {
  trackEvent('navigation', {
    from_page: from,
    to_page: to,
    navigation_method: method,
  });
};

/**
 * Track file downloads
 */
export const trackDownload = (
  fileName: string,
  fileType: string,
  context?: string
): void => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    ...(context && { context }),
  });
};

/**
 * Track error occurrences
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  context?: string,
  severity: 'low' | 'medium' | 'high' | 'critical' = 'medium'
): void => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    severity,
    ...(context && { context }),
  });
};

// ============================================
// PERFORMANCE TRACKING
// ============================================

/**
 * Track page load performance
 */
export const trackPageLoadTime = (page?: string): void => {
  if (!isGAInitialized()) return;

  try {
    const perfData = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (perfData) {
      const loadTime = perfData.loadEventEnd - perfData.fetchStart;
      const domContentLoaded = perfData.domContentLoadedEventEnd - perfData.fetchStart;
      const domInteractive = perfData.domInteractive - perfData.fetchStart;

      trackEvent('page_performance', {
        page: page || window.location.pathname,
        load_time: Math.round(loadTime),
        dom_content_loaded: Math.round(domContentLoaded),
        dom_interactive: Math.round(domInteractive),
      });
    }
  } catch (error) {
    console.error('[GA4] Page load time tracking failed:', error);
  }
};

/**
 * Track API response time
 */
export const trackAPIPerformance = (
  endpoint: string,
  method: string,
  responseTime: number,
  statusCode: number,
  success: boolean
): void => {
  trackEvent('api_performance', {
    endpoint,
    method,
    response_time: Math.round(responseTime),
    status_code: statusCode,
    success,
  });
};

/**
 * Track component render time
 */
export const trackComponentRender = (
  componentName: string,
  renderTime: number
): void => {
  trackEvent('component_render', {
    component_name: componentName,
    render_time: Math.round(renderTime),
  });
};

/**
 * Track Core Web Vitals
 */
export const trackWebVitals = (metric: {
  name: 'CLS' | 'FID' | 'FCP' | 'LCP' | 'TTFB';
  value: number;
  id: string;
  delta: number;
}): void => {
  trackEvent('web_vitals', {
    metric_name: metric.name,
    metric_value: Math.round(metric.value),
    metric_id: metric.id,
    metric_delta: Math.round(metric.delta),
  });
};

/**
 * Track resource loading times
 */
export const trackResourceLoad = (
  resourceName: string,
  resourceType: string,
  loadTime: number
): void => {
  trackEvent('resource_load', {
    resource_name: resourceName,
    resource_type: resourceType,
    load_time: Math.round(loadTime),
  });
};

/**
 * Start performance measurement
 */
export const startPerformanceMeasure = (measureName: string): void => {
  if (typeof window !== 'undefined' && window.performance) {
    performance.mark(`${measureName}-start`);
  }
};

/**
 * End performance measurement and track
 */
export const endPerformanceMeasure = (
  measureName: string,
  context?: string
): number | null => {
  if (typeof window !== 'undefined' && window.performance) {
    try {
      performance.mark(`${measureName}-end`);
      performance.measure(
        measureName,
        `${measureName}-start`,
        `${measureName}-end`
      );

      const measure = performance.getEntriesByName(measureName)[0];
      const duration = measure.duration;

      trackEvent('custom_performance', {
        measure_name: measureName,
        duration: Math.round(duration),
        ...(context && { context }),
      });

      // Clean up marks and measures
      performance.clearMarks(`${measureName}-start`);
      performance.clearMarks(`${measureName}-end`);
      performance.clearMeasures(measureName);

      return duration;
    } catch (error) {
      console.error('[GA4] Performance measurement failed:', error);
      return null;
    }
  }
  return null;
};

/**
 * Track memory usage (if available)
 */
export const trackMemoryUsage = (): void => {
  if (typeof window !== 'undefined' && (performance as any).memory) {
    const memory = (performance as any).memory;
    trackEvent('memory_usage', {
      used_js_heap_size: Math.round(memory.usedJSHeapSize / 1048576), // Convert to MB
      total_js_heap_size: Math.round(memory.totalJSHeapSize / 1048576),
      js_heap_size_limit: Math.round(memory.jsHeapSizeLimit / 1048576),
    });
  }
};

// ============================================
// ADMIN-SPECIFIC TRACKING
// ============================================

/**
 * Track admin actions
 */
export const trackAdminAction = (
  action: string,
  targetType: 'user' | 'course' | 'organization' | 'workflow' | 'gamification' | 'other',
  targetId?: string,
  additionalData?: GAEventParams
): void => {
  trackEvent('admin_action', {
    action,
    target_type: targetType,
    ...(targetId && { target_id: targetId }),
    ...additionalData,
  });
};

/**
 * Track data table interactions
 */
export const trackTableAction = (
  tableName: string,
  action: 'view' | 'create' | 'edit' | 'delete' | 'export' | 'import' | 'bulk_action',
  rowCount?: number
): void => {
  trackEvent('table_action', {
    table_name: tableName,
    action,
    ...(rowCount !== undefined && { row_count: rowCount }),
  });
};

/**
 * Track dashboard widget interactions
 */
export const trackDashboardWidget = (
  widgetName: string,
  action: 'view' | 'refresh' | 'configure' | 'expand' | 'collapse'
): void => {
  trackEvent('dashboard_widget', {
    widget_name: widgetName,
    action,
  });
};

// Export all functions as default analytics object
const analytics = {
  // Core functions
  init: initGA,
  isInitialized: isGAInitialized,
  pageView: trackPageView,
  event: trackEvent,
  setUserProperties,
  setUserId,
  login: trackLogin,
  logout: trackLogout,

  // User behavior tracking
  click: trackClick,
  formSubmit: trackFormSubmit,
  formInteraction: trackFormInteraction,
  search: trackSearch,
  filter: trackFilter,
  sort: trackSort,
  modal: trackModal,
  tabChange: trackTabChange,
  scrollDepth: trackScrollDepth,
  timeOnPage: trackTimeOnPage,
  navigation: trackNavigation,
  download: trackDownload,
  error: trackError,

  // Performance tracking
  pageLoadTime: trackPageLoadTime,
  apiPerformance: trackAPIPerformance,
  componentRender: trackComponentRender,
  webVitals: trackWebVitals,
  resourceLoad: trackResourceLoad,
  startMeasure: startPerformanceMeasure,
  endMeasure: endPerformanceMeasure,
  memoryUsage: trackMemoryUsage,

  // Admin-specific tracking
  adminAction: trackAdminAction,
  tableAction: trackTableAction,
  dashboardWidget: trackDashboardWidget,
};

export default analytics;
